/**
 * @copyright (C) 2023, Rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of Rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by Rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/
/* eslint @typescript-eslint/no-explicit-any: 0 */
/* eslint @typescript-eslint/no-non-null-assertion: 0 */

/**
 * A generic Queue implementation.
 *
 * @template T - The type of elements held in the queue.
 */
export default class Queue<T = any> {
	private _queue: T[];

	constructor() {
		this._queue = [];
	}

	/**
	 * Adds an item to the queue.
	 *
	 * @param {T} item - The item to be added to the queue.
	 * @return {void} This method does not return a value.
	 */
	public push(item: T): void {
		this._queue.push(item);
	}

	/**
	 * Removes and returns the first element from the queue.
	 *
	 * @return {T | undefined} The removed element from the queue if present, otherwise undefined.
	 */
	public pop(): T | undefined {
		return this._queue.shift();
	}

	/**
	 * Retrieves the current size of the queue.
	 *
	 * @return The number of elements in the queue.
	 */
	public get size(): number {
		return this._queue!.length;
	}

	/**
	 * Checks if the collection is empty.
	 *
	 * @return {boolean} Returns true if the collection is empty, otherwise false.
	 */
	public get empty(): boolean {
		return this.size <= 0;
	}

	/**
	 * Retrieves the current state of the queue.
	 *
	 * @return {T[]} The array representing the current elements in the queue.
	 */
	public get queue(): T[] {
		return this._queue;
	}

	/**
	 * Clears all items from the internal queue, leaving it empty.
	 *
	 * @return {void}
	 */
	public clear(): void {
		this._queue = [];
	}
}
